"use strict";
// @voris/analytics — browser SDK
//
// Persists an anonymous_id in localStorage, batches events in memory,
// flushes on a 5s timer + on visibility/unload via fetch keepalive.
// Network failures drop the batch (analytics is best-effort).
Object.defineProperty(exports, "__esModule", { value: true });
exports._flush = exports.page = exports.identify = exports.track = exports.init = void 0;
const ANON_KEY = '_voris_aid';
const SESSION_KEY = '_voris_sid';
// 30-min idle window. Beyond that we mint a new session_id.
const SESSION_TTL_MS = 30 * 60 * 1000;
const QUEUE_HIGH_WATER = 20;
let cfg = {
    writeKey: '',
    apiUrl: 'https://api.fvoris.com',
    flushIntervalMs: 5000,
    debug: false,
};
let queue = [];
let userId = null;
let initialized = false;
let flushTimer = null;
function generateId() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}
function getAnonId() {
    let id = localStorage.getItem(ANON_KEY);
    if (!id) {
        id = generateId();
        localStorage.setItem(ANON_KEY, id);
    }
    return id;
}
function getSessionId() {
    const now = Date.now();
    const stored = localStorage.getItem(SESSION_KEY);
    if (stored) {
        const sep = stored.indexOf('|');
        if (sep > 0) {
            const id = stored.slice(0, sep);
            const lastTs = Number(stored.slice(sep + 1));
            if (id && lastTs && now - lastTs < SESSION_TTL_MS) {
                localStorage.setItem(SESSION_KEY, `${id}|${now}`);
                return id;
            }
        }
    }
    const id = generateId();
    localStorage.setItem(SESSION_KEY, `${id}|${now}`);
    return id;
}
function enqueue(eventName, properties = {}) {
    if (!initialized)
        return;
    queue.push({
        event_name: eventName,
        anonymous_id: getAnonId(),
        user_id: userId,
        session_id: getSessionId(),
        properties,
        page_url: location.href,
        referrer: document.referrer || undefined,
        ts: new Date().toISOString(),
    });
    if (queue.length >= QUEUE_HIGH_WATER) {
        void flush();
    }
}
async function flush() {
    if (queue.length === 0)
        return;
    const batch = queue;
    queue = [];
    try {
        await fetch(`${cfg.apiUrl}/v1/events`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${cfg.writeKey}`,
            },
            body: JSON.stringify({ events: batch }),
            // Survives short-lived unloads. Browsers cap keepalive bodies (~64KB),
            // which fits the 64KB server limit.
            keepalive: true,
        });
    }
    catch (err) {
        if (cfg.debug) {
            console.warn('[voris] flush failed', err);
        }
    }
}
function setupFlushTriggers() {
    if (flushTimer)
        clearInterval(flushTimer);
    flushTimer = setInterval(() => void flush(), cfg.flushIntervalMs);
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden')
            void flush();
    });
    window.addEventListener('pagehide', () => void flush());
}
function init(opts) {
    cfg = {
        writeKey: opts.writeKey,
        apiUrl: opts.apiUrl ?? 'https://api.fvoris.com',
        flushIntervalMs: opts.flushIntervalMs ?? 5000,
        debug: opts.debug ?? false,
    };
    initialized = true;
    setupFlushTriggers();
}
exports.init = init;
function track(eventName, properties) {
    enqueue(eventName, properties);
}
exports.track = track;
function identify(uid, traits) {
    userId = uid;
    enqueue('identify', traits);
}
exports.identify = identify;
function page(name, properties) {
    const props = { ...(properties || {}) };
    if (name)
        props.name = name;
    enqueue('pageview', props);
}
exports.page = page;
// Test/debug utility — not for production use.
function _flush() {
    return flush();
}
exports._flush = _flush;
